/** @jsx jsx */

import * as React from "react";
import { css, jsx } from "@emotion/core";
import { NavItem, NavItemProps } from "./nav_item";
import { v4 as uuid } from "uuid";
import { DESIGN_TOKEN } from "Config/design_token";
import { Hamburger } from "./hamburger";
require("Views/assets/sandpiper_logo.svg");

/** Props required for instantiating the main navigation. */
export type NavProps = {
  /**
   * If the navigation should render as a navigation bar or a hamburger
   * menu, defaults to "COMPACT".
   */
  layout: "COMPACT" | "FULL";
  /**
   * An array of navigation item information to render as buttons, links, etc.
   */
  items: Array<Pick<NavItemProps, "destination" | "title" | "isActive">>;
  /**
   * Callback that fires when a navigation element is clicked.  The element's
   * destination path is passed to the callback.
   */
  onItemClick: (destination: NavItemProps["destination"]) => void;
  /** Optional callback that fires when the menu button is clicked. */
  onExpand?: (expand: boolean) => void;
};

function mapNavItems(
  items: NavProps["items"],
  clickHandler: NavProps["onItemClick"],
  layout: NavProps["layout"]
): React.ReactFragment {
  const navItems = items.map(item => (
    <li
      key={uuid()}
      css={css`
        padding: 0rem;
        margin: 0rem;
        border-top: thin solid ${DESIGN_TOKEN.colors.swatch_5};
      `}
    >
      <NavItem
        title={item.title}
        destination={item.destination}
        isActive={item.isActive}
        onClick={destination => clickHandler(destination)}
        layout={layout}
      />
    </li>
  ));
  return navItems;
}

function Nav(props: NavProps): JSX.Element {
  const {
    layout = "COMPACT",
    items = [],
    onItemClick,
    onExpand = () => {}
  } = props;

  const [isOpen, setOpen] = React.useState(false);

  return (
    <nav
      role="navigation"
      css={css`
        display: flex;
        position: absolute;
        width: 100%;
        height: auto;
        min-height: 100%;
        left: ${isOpen ? "0%" : "-100%"};
        top: 0rem;
        z-index: 99;
        flex-shrink: 0;
        flex-grow: 0;
        flex-direction: column;
        padding: 0rem;
        background-color: ${DESIGN_TOKEN.colors.swatch_3};
        align-items: center;
        justify-content: flex-start;
        transition: left 0.3s ease;
        ${layout === "FULL" &&
          `
          position: relative;
          left: 0rem;
          width: 15rem;
        `}
      `}
    >
      {layout === "COMPACT" ? (
        <React.Fragment>
          <Hamburger
            isOpen={isOpen}
            onClick={() => {
              onExpand(!isOpen);
              setOpen(!isOpen);
            }}
            aria-controls="navigation_menu"
          />
        </React.Fragment>
      ) : (
        <div
          css={css`
            padding: 2rem;
            width: 100%;
            height: auto;
          `}
        >
          <a href="/">
            <img
              css={css`
                width: 100%;
                height: auto;
              `}
              src="/wp-content/themes/sandpiper/img/sandpiper_logo.svg"
              alt="The Sandpiper Nursery School"
            />
          </a>
        </div>
      )}
      <ul
        id="navigation_menu"
        css={css`
          display: flex;
          flex-direction: column;
          width: 100%;
          margin: 0rem;
          padding: 0rem;
          list-style-type: none;
        `}
      >
        {mapNavItems(items, onItemClick, layout)}
      </ul>
    </nav>
  );
}

const navMemo = React.memo<NavProps>(Nav);
export { navMemo as Nav };
